"use client";

/**
 * NicheDetailV2 — Niche v3 Detail scrolltell wrapper.
 *
 * Route: `/niche/{sessionId}/c/{candidateId}` (page-level composition).
 * Spec: wireframe-v3.md §1 + §3 (6 scenes + Wrapped) + §4 (sticky nav).
 *
 * Composition (mount order):
 *   TopBar (back link)
 *   StickyNavRail (shared — left rail desktop, top pill mobile) — 6 scenes + Wrapped
 *   Scene 0  OpeningHookScene
 *   Scene 1  CustomerScene
 *   Scene 2  MarketScene (WaterfallScrollPin)
 *   Scene 3  BattlefieldScene   (agent C — dynamic import)
 *   Scene 4  PathForwardScene   (agent C — dynamic import)
 *   Scene 5  DecideScene        (agent C — dynamic import)
 *   WrappedFinale overlay       (agent C — dynamic import, triggered manually)
 *
 * Scroll tracking:
 *   IntersectionObserver picks the scene with the highest intersection
 *   ratio as the active one and feeds it to StickyNavRail.
 *
 * Keyboard:
 *   Escape       → onBack()
 *   ArrowDown    → scroll to next scene
 *   ArrowUp      → scroll to previous scene
 *
 * Dynamic imports: agent C is creating Battlefield / PathForward / Decide /
 * WrappedFinale in parallel. We load them via `next/dynamic` with ssr:false
 * and `loading: () => null` so TypeScript doesn't fail if the files don't
 * exist yet at typecheck time (next/dynamic only imports at runtime).
 *
 * Props shape intentionally mirrors the wrapper-style pattern (BrandPresentationV2 /
 * PlanPresentationV2) — parent page.tsx passes resolved data + handlers.
 */

import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NicheCandidateV3, SessionMetaV3 } from "@/types/niche-v3";
import type { ParsedDebate } from "@/lib/debate-types";
import { trackEventSessionOnce } from "@/lib/analytics";
import { captureActivity } from "@/lib/activity-client";
import {
  StickyNavRail,
  type StickyNavSection,
} from "@/components/shared/StickyNavRail";
import { SiteFooter } from "@/components/shared/SiteFooter";
import { ArtifactTopBar } from "@/components/shared/ArtifactTopBar";
import { VisibilityToggle } from "@/components/shared/VisibilityToggle";
import { OpeningHookScene } from "./scenes/OpeningHookScene";
import { CustomerScene } from "./scenes/CustomerScene";
import { MarketScene } from "./scenes/MarketScene";

/* ─── Dynamic scene imports (agent C, parallel) ─── */

// Battlefield / PathForward / Decide / WrappedFinale are authored by a
// parallel agent. Use next/dynamic so typecheck + bundling work even if the
// files temporarily don't exist. If a scene fails to import at runtime, we
// render `null` (no broken UI).
const BattlefieldScene = dynamic<{
  niche: NicheCandidateV3;
  debate: ParsedDebate | null;
}>(
  () =>
    import("./scenes/BattlefieldScene").then((m) => ({
      default: m.BattlefieldScene,
    })),
  { ssr: false, loading: () => null },
);

const PathForwardScene = dynamic<{
  niche: NicheCandidateV3;
  debate: ParsedDebate | null;
}>(
  () =>
    import("./scenes/PathForwardScene").then((m) => ({
      default: m.PathForwardScene,
    })),
  { ssr: false, loading: () => null },
);

const DecideScene = dynamic<{
  niche: NicheCandidateV3;
  debate?: ParsedDebate | null;
  onDive: (otherCandidateId: string) => void;
  onBack: () => void;
  sessionId: string;
  candidateId: string;
  survivorsCount?: number;
  credits?: number;
  isLoggedIn?: boolean;
  viewerRole?: import("@/lib/artifact-viewer-role").ViewerRole;
  onShowWrapped?: () => void;
  updatedAt?: string;
  planArtifact?: {
    id: string;
    status: "generating" | "pending" | "complete" | "failed";
  } | null;
}>(
  () =>
    import("./scenes/DecideScene").then((m) => ({
      default: m.DecideScene,
    })),
  { ssr: false, loading: () => null },
);

const WrappedFinale = dynamic(
  () =>
    import("./WrappedFinale").then((m) => ({
      default: m.WrappedFinale,
    })),
  { ssr: false, loading: () => null },
);

/* ─── Scene list (6 scenes + Wrapped icon) ─── */

const SCENES: StickyNavSection[] = [
  { id: "scene-0-opening-hook", label: "Opening", kind: "default" },
  { id: "scene-1-customer", label: "Customer", kind: "default" },
  { id: "scene-2-market", label: "Market", kind: "default" },
  { id: "scene-3-battlefield", label: "Battle", kind: "default" },
  { id: "scene-4-path-forward", label: "Path", kind: "default" },
  { id: "scene-5-decide", label: "Decide", kind: "default" },
  { id: "wrapped", label: "Wrapped", kind: "wrapped" },
];

const SCENE_SCROLL_IDS = SCENES.filter((s) => s.kind !== "wrapped").map(
  (s) => s.id,
);

/* ─── Props ─── */

interface NicheDetailV2Props {
  sessionId: string;
  candidateId: string;
  niche: NicheCandidateV3;
  debate: ParsedDebate | null;
  sessionMeta: SessionMetaV3;
  /** Back-to-Grid handler. */
  onBack: () => void;
  /** Dive into another niche (used by DecideScene). */
  onDive: (otherCandidateId: string) => void;
  /** Optional credit balance — gates DecideScene retry CTA. */
  credits?: number;
  /** Optional auth flag — DecideScene routes logged-out users to login. */
  isLoggedIn?: boolean;
  /** Session updated_at / created_at — share-card cache-bust. */
  updatedAt?: string;
  planArtifact?: {
    id: string;
    status: "generating" | "pending" | "complete" | "failed";
  } | null;
  /** Owner flag — when true, renders the VisibilityToggle in the top bar. */
  isOwner?: boolean;
  /** Initial public/private state (default true for legacy sessions). */
  initialIsPublic?: boolean;
  /** Whether the user's tier permits switching to private. */
  tierAllowsPrivate?: boolean;
  /** Viewer-role classifier — forwarded to DecideScene's action block. */
  viewerRole?: import("@/lib/artifact-viewer-role").ViewerRole;
}

/* ─── Component ─── */

export function NicheDetailV2({
  sessionId,
  candidateId,
  niche,
  debate,
  sessionMeta,
  onBack,
  onDive,
  credits,
  isLoggedIn,
  updatedAt,
  planArtifact,
  isOwner = false,
  initialIsPublic = true,
  tierAllowsPrivate = false,
  viewerRole,
}: NicheDetailV2Props) {
  const [activeSceneId, setActiveSceneId] = useState<string>(
    SCENES[0].id,
  );
  const [wrappedOpen, setWrappedOpen] = useState(false);
  /**
   * Auto-open is one-shot: first time Scene 5 IntersectionObserver (or #wrapped
   * hash deeplink) triggers an automatic open + close cycle, further auto-opens
   * are suppressed. The manual "See your story" button always works.
   */
  const autoOpenConsumedRef = useRef(false);

  /* Active-scene tracking is folded into the unified scroll listener below
   * (scroll-spy via getBoundingClientRect). The earlier IntersectionObserver
   * with `rootMargin: -30% -60%` had a 10% middle band; tall scenes
   * (especially PathForwardScene) could fully straddle that band on slow
   * scroll without crossing it, leaving `bestRatio=0` and the rail stuck on
   * the previous active. Scroll-spy is more robust for variable-height
   * scenes and lets us share the rAF tick with back-bar autohide. */

  /* Jump handler — scene → smooth scroll, wrapped → open overlay. */
  const handleJump = useCallback((id: string) => {
    if (id === "wrapped") {
      setWrappedOpen(true);
      return;
    }
    const el =
      typeof document !== "undefined"
        ? document.getElementById(id)
        : null;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  /* Keyboard: Escape → back, ArrowDown/Up → next/prev scene. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: KeyboardEvent) => {
      // Don't intercept when the user is typing in a form control.
      const t = e.target as HTMLElement | null;
      if (t && /^(input|textarea|select)$/i.test(t.tagName)) return;

      if (e.key === "Escape") {
        if (wrappedOpen) {
          autoOpenConsumedRef.current = true;
          setWrappedOpen(false);
          return;
        }
        e.preventDefault();
        onBack();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        // Only jump between scenes when NOT already inside a scroll-pin drag
        // (we don't have a good signal for that; keep behaviour simple).
        const curIdx = SCENE_SCROLL_IDS.indexOf(activeSceneId);
        const nextIdx =
          e.key === "ArrowDown"
            ? Math.min(SCENE_SCROLL_IDS.length - 1, curIdx + 1)
            : Math.max(0, curIdx - 1);
        if (nextIdx !== curIdx) {
          e.preventDefault();
          handleJump(SCENE_SCROLL_IDS[nextIdx]);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeSceneId, wrappedOpen, onBack, handleJump]);

  /* Deeplink: #wrapped → auto-open overlay on mount (wireframe §3.6 trigger).
   * One-shot: consume the auto-open token so we don't re-fire on hash changes. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (
      window.location.hash === "#wrapped" &&
      !autoOpenConsumedRef.current
    ) {
      autoOpenConsumedRef.current = true;
      const timer = window.setTimeout(() => setWrappedOpen(true), 0);
      return () => window.clearTimeout(timer);
    }
  }, []);

  /* Active-scene scroll-spy + near-bottom guard.
   *
   * Back-bar autohide is owned by <ArtifactTopBar>; what's left here is the
   * scene-tracking logic for StickyNavRail. The earlier IntersectionObserver
   * (rootMargin "-30% -60%") has a 10% middle band; tall scenes can fully
   * straddle that band on slow scroll without crossing it, leaving
   * `bestRatio=0`. Scroll-spy via getBoundingClientRect is more robust for
   * variable-height scenes.
   *
   * Near-bottom guard: within 200px of doc end, no scene crosses the pin
   * line so we'd freeze on whichever scene was last detected. Force the
   * last narrative scene (scene-5-decide) so StickyNavRail aligns with the
   * 100% progress bar in the footer band. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    let ticking = false;
    const lastSceneId = SCENE_SCROLL_IDS[SCENE_SCROLL_IDS.length - 1];
    /** Pin-line for active-scene detection (~ navbar + back-bar zone). */
    const PIN_LINE = 120;
    const computeActiveSceneId = (): string | null => {
      let activeId: string | null = null;
      for (const id of SCENE_SCROLL_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= PIN_LINE) activeId = id;
      }
      return activeId;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const nearBottom =
          window.innerHeight + y >= document.body.scrollHeight - 200;
        const nextActive = nearBottom
          ? lastSceneId
          : computeActiveSceneId();
        if (nextActive) {
          setActiveSceneId((prev) =>
            prev === nextActive ? prev : nextActive,
          );
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof IntersectionObserver === "undefined") return;
    const fired = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
          const sceneId = entry.target.id;
          if (!sceneId || fired.has(sceneId)) continue;
          fired.add(sceneId);
          const position = SCENE_SCROLL_IDS.indexOf(sceneId);
          trackEventSessionOnce(
            "niche.scene_viewed",
            `${candidateId}:${sceneId}`,
            {
              scene_id: sceneId,
              niche_id: candidateId,
              position: position >= 0 ? position : undefined,
            },
          );
          captureActivity({
            eventType: "artifact.section_viewed",
            artifactType: "niche",
            artifactId: candidateId,
            sessionId,
            metadata: {
              scene_id: sceneId,
              position: position >= 0 ? position : null,
            },
          });
        }
      },
      { threshold: 0.5 },
    );
    for (const id of SCENE_SCROLL_IDS) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [candidateId, sessionId]);

  /* Manual trigger — always re-opens, even after auto-open was consumed. */
  const handleManualShowWrapped = useCallback(() => {
    setWrappedOpen(true);
  }, []);

  const handleWrappedClose = useCallback(() => {
    // Consume token on first close so a later auto-trigger doesn't re-open.
    autoOpenConsumedRef.current = true;
    setWrappedOpen(false);
  }, []);

  const ariaLabel = useMemo(
    () => `Niche detail: ${niche.name}`,
    [niche.name],
  );

  return (
    <>
      <main
        aria-label={ariaLabel}
        // `overflow-x-clip` (not `hidden`): `hidden` coerces overflow-y to auto
        // per CSS spec, turning <main> into a scroll container which breaks
        // `position: sticky` for descendants (MarketScene WaterfallScrollPin
        // stopped pinning; bars stuck at 0% width, narrative frozen on TAM).
        // `clip` suppresses horizontal overflow without becoming a scroll
        // container — sticky descendants keep the viewport as their scroll
        // ancestor. Baseline browser support is fine (Chrome 90+, FF 81+,
        // Safari 16+). (S72 fix)
        className="relative min-h-screen w-full overflow-x-clip bg-surface text-white"
      >
      {/* ─── Top bar ─── shared component handles autohide, flush-to-navbar
       * placement (consumes `--layout-navbar-h` published by NavbarShell),
       * and publishes `--waterfall-pin-top` for the mobile chart pin. */}
      <ArtifactTopBar
        leftSlot={
          <button
            type="button"
            onClick={onBack}
            className={cn(
              "inline-flex items-center gap-2 rounded-full",
              "border border-white/10 bg-surface/60 px-4 py-2",
              "text-[13px] font-medium text-white/70 backdrop-blur",
              "transition-colors hover:border-amber-400/40 hover:text-white",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50",
            )}
            aria-label="Back to all niches"
          >
            <ArrowLeft className="size-4" aria-hidden />
            <span>Back</span>
          </button>
        }
        rightSlot={
          <>
            {/* Candidate meta label — small, ambient. Id truncated to 8 chars. */}
            <span
              className="hidden text-[11px] uppercase tracking-[0.18em] text-white/30 lg:inline"
              aria-hidden
            >
              {candidateId.slice(0, 8)}
            </span>
            {isOwner && (
              <VisibilityToggle
                artifactType="session"
                artifactId={sessionId}
                initialIsPublic={initialIsPublic}
                tierAllowsPrivate={tierAllowsPrivate}
              />
            )}
          </>
        }
      />

      {/* ─── Sticky nav ─── */}
      <StickyNavRail
        sections={SCENES}
        activeSectionId={activeSceneId}
        onJump={handleJump}
        ariaLabel="Niche scenes"
        listId="niche-nav-mobile-list"
      />

      {/* ─── Scenes ─── */}
      <OpeningHookScene
        niche={niche}
        searchConfidence={sessionMeta.searchConfidence}
      />
      <CustomerScene niche={niche} debate={debate} />
      <MarketScene niche={niche} />
      <BattlefieldScene niche={niche} debate={debate} />
      <PathForwardScene niche={niche} debate={debate} />
      <DecideScene
        niche={niche}
        debate={debate}
        onDive={onDive}
        onBack={onBack}
        sessionId={sessionId}
        candidateId={candidateId}
        survivorsCount={sessionMeta.nichesSurvived}
        credits={credits}
        isLoggedIn={isLoggedIn}
        viewerRole={viewerRole}
        onShowWrapped={handleManualShowWrapped}
        updatedAt={updatedAt}
        planArtifact={planArtifact}
      />

      {/* ─── Wrapped overlay ─── */}
      <WrappedFinale
        niche={niche}
        sessionId={sessionId}
        candidateId={candidateId}
        open={wrappedOpen}
        onClose={handleWrappedClose}
        debate={debate}
        updatedAt={updatedAt}
      />
      </main>
      <SiteFooter variant="compact" />
    </>
  );
}
